import styled from "styled-components";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { useCallback, useEffect, useRef, useState } from "react";
import { colors } from "../../../theme/colors";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { Row } from "../../../components/ui/grid/row.component";
import {
  MdArrowBack,
  MdArrowForward,
  MdCheckCircleOutline,
} from "react-icons/md";
import { Column } from "../../../components/ui/grid/column.component";
import { Subject, debounceTime } from "rxjs";
import { Button, Form, Tooltip } from "antd";
import { Season } from "../../../models/season.model";
import { Splash } from "../../../components/ui/splash/splash.component";
import { useNavigate, useParams } from "react-router-dom";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { useForm } from "antd/es/form/Form";
import {
  StepCompletionStatus,
  StepNav,
} from "../../films/components/step-nav.component";
import { card } from "../../films/components/section-card.component";
import { Step } from "../sections/step.section";
import { Poster } from "../../../types/Poster";
import toast from "react-hot-toast";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { axios } from "../../../api/axios";
import dayjs, { Dayjs } from "dayjs";
import { v4 } from "uuid";
import { Award } from "../../../models/film-profile.model";
import { SeriesProfile } from "../../../models/series-profile.model";
import moment from "moment";
import { releaseYearValue } from "../utils/year";
import { episodeDurationValue, episodeDurationParts } from "../utils/duration";
import { wait } from "../../../utils/functions/misc/wait";
import { sluggify } from "../../../utils/functions/string/sluggify";
import {
  CUSTOM_NAME_MAX_LENGTH,
  getCustomNameValidationError,
  sanitizeCustomName,
} from "../../../utils/functions/string/custom-url";
import useModal from "antd/es/modal/useModal";
import {
  hasPosterTypeZero,
  tabForEpisodeErrors,
  validateSeriesDraft,
} from "../utils/validation";

const sections = {
  language: 0,
  genre: 0,
  synopsis: 0,
  title: 0,
  english_title: 0,
  custom_name: 0,
  language_rating: 0,
  violence_rating: 0,
  hateful_rating: 0,
  adult_content_rating: 0,
  drug_rating: 0,
  harmful_rating: 0,
};

const customUrlSubject$ = new Subject<string>();
const professionalProfileSubject$ = new Subject<string>();

/** After a draft save, merge server-assigned season/episode ids into local state. */
function syncSeasonIdsFromApi(
  local: Season[],
  fromApi: SeriesProfile["seasons"]
): Season[] {
  if (!fromApi?.length) return local;

  return local.map((season, index) => {
    const remote =
      fromApi.find((s) => s.id && s.id === season.id) ??
      fromApi.find(
        (s) =>
          s.id &&
          s.season_number === season.season_number &&
          !local.some((other) => other !== season && other.id === s.id)
      ) ??
      fromApi[index];
    if (!remote?.id || local.some((other, otherIndex) => otherIndex < index && other.id === remote.id)) {
      return season;
    }

    return {
      ...season,
      ...(remote.id ? { id: remote.id } : {}),
      episodes: season.episodes.map((episode, epIndex) => {
        const remoteEp =
          remote.episodes?.find(
            (e) => e.episode_number === episode.episode_number
          ) ?? remote.episodes?.[epIndex];
        if (!remoteEp?.id) return episode;
        return { ...episode, id: remoteEp.id };
      }),
    };
  });
}

export const CreateSeries = ({ editing }: { editing?: boolean }) => {
  const { id: seriesId } = useParams();

  const navigate = useNavigate();

  const [modal, modalConfig] = useModal();

  const [loaded, setLoaded] = useState(false);
  const [seriesObj, setSeriesObj] = useState<SeriesProfile | null>(null);

  const [isDraft, setIsDraft] = useState(false);

  const [urlVerified, setUrlVerified] = useState<boolean | null>(null);
  const [showVerification, setShowVerification] = useState(true);
  const [customUrl, setCustomUrl] = useState("");

  const [seasons, setSeasons] = useState<Season[]>([
    {
      season_number: 1,
      id: v4(),
      modified: false,
      season_title: "",
      season_synopsis: "",
      release_status: "unreleased",
      episodes: [
        {
          modified: false,
          title: "",
          duration: "0:0",
          episode_number: 1,
          release_date: "",
          cast: [],
          crew: [],
          images: [],
          vertical_poster: [],
          horizontal_poster: [],
          synopsis: "",
          shooting_format: "",
          aspect_ratio: "",
          id: v4(),
          ratings: {
            censor: "",
            adultContent: "",
            language: "",
            violence: "",
            drugs: "",
            harmful: "",
            hateful: "",
          },
          awards: [],
        },
      ],
      awards: [],
      vertical_poster: [],
      horizontal_poster: [],
    },
  ]);
  const [activeSeasonIndex, setActiveSeasonIndex] = useState<number>(0);
  const [activeEpisodeIndex, setActiveEpisodeIndex] = useState<number>(0);
  const [isDistributed, setIsDistributed] = useState(false);

  const [posterErrors, setPosterErrors] = useState<[boolean, boolean]>([
    false,
    false,
  ]);

  const [seasonPosterErrors, setSeasonPosterErrors] = useState<
    [boolean, boolean][]
  >([]);
  const [episodePosterErrors, setEpisodePosterErrors] = useState<
    [boolean, boolean][]
  >([]);

  const [stepFourTab, setStepFourTab] = useState<0 | 1>(0);
  const [stepFiveTab, setStepFiveTab] = useState<0 | 1 | 2>(0);

  const [stepFourShowErrors, setStepFourShowErrors] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<StepCompletionStatus[]>(
    Array(5).fill(null)
  );

  const customUrlInput = useRef<HTMLInputElement | null>(null);

  const [title, setTitle] = useState("");
  const titleSpan = useRef<HTMLSpanElement | null>(null);
  const [titleWidth, setTitleWidth] = useState(0);

  const [synopsis, setSynopsis] = useState("");

  const [verticalPoster, setVerticalPoster] = useState<Poster[]>([]);
  const [horizontalPoster, setHorizontalPoster] = useState<Poster[]>([]);

  useEffect(() => {
    const hasRequiredPosters =
      hasPosterTypeZero(verticalPoster) && hasPosterTypeZero(horizontalPoster);

    if (hasRequiredPosters) {
      setPosterErrors([false, false]);
      setCompletedSteps((prev) => {
        if (prev[1] !== "warning") return prev;
        const next = [...prev];
        next[1] = true;
        return next;
      });
    }
  }, [verticalPoster, horizontalPoster]);

  const [nameOptions, setNameOptions] = useState<
    {
      value: string;
      label: string;
      logo?: string;
      id?: string;
      full_name?: string;
      custom_url?: string;
      type?: "professional" | "business";
    }[]
  >([]);

  const [showSuccess, setShowSuccess] = useState(false);

  // Track unsaved changes for beforeunload warning
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const hasTriggeredAutosave = useRef(false);
  const beforeUnloadTriggered = useRef(false);

  const [form] = useForm<SeriesProfile>();

  const onSeriesTitleChange = useCallback(
    (newTitle: string) => {
      const currentCustomUrl =
        customUrl.trim() ||
        String(form.getFieldValue("custom_name") ?? "").trim();

      if (!currentCustomUrl) {
        const slug = sanitizeCustomName(
          sluggify(newTitle, "-"),
          CUSTOM_NAME_MAX_LENGTH
        );
        form.setFieldValue("custom_name", slug);
        customUrlSubject$.next(slug);
      }

      if (!form.isFieldTouched("english_title")) {
        form.setFieldValue("english_title", newTitle);
      }
    },
    [form, customUrl]
  );

  const onSubmit = useCallback(
    async (editing = false, draft = false): Promise<boolean> => {
      if (!synopsis) {
        form.setFields([{ name: "synopsis", errors: ["Please enter a synopsis!"] }]);
        if (!draft) {
          toast.error("Please enter a synopsis!");
          setCurrentStep(0);
        }
        return false;
      }

      if (!customUrl) {
        toast.error("Please enter a custom URL!");
        return false;
      }

      const profileValidation = validateSeriesDraft({
        film: { ...form.getFieldsValue(), title },
        synopsis,
        verticalPoster,
        horizontalPoster,
        seasons,
      });

      if (!draft && !profileValidation.isValid) {
        toast.error(profileValidation.firstError ?? "Please fix all errors before submitting!");
        const seasonError = profileValidation.seasonErrors[0];
        const episodeError = profileValidation.episodeErrors[0];
        if (profileValidation.seriesErrors.length) {
          setCurrentStep(0);
        } else if (seasonError) {
          setActiveSeasonIndex(seasonError.seasonIndex);
          setCurrentStep(2);
        } else if (episodeError) {
          setActiveSeasonIndex(episodeError.seasonIndex);
          setActiveEpisodeIndex(episodeError.episodeIndex);
          setStepFiveTab(tabForEpisodeErrors(episodeError.errors));
          setCurrentStep(3);
        } else {
          setCurrentStep(0);
        }
        return false;
      }

      try {
        if (!draft) {
          await form.validateFields();
        }
      } catch (_error) {
        if (!draft) {
          toast.error("Please fix the errors in the form!");
          setCurrentStep(0);
        }
        return false;
      }

      const profile = form.getFieldsValue();
      profile.id = seriesId ?? "";
      profile.title = title;
      profile.status = draft ? "draft" : "published";
      profile.seasons = seasons as any;
      profile.synopsis = synopsis;

      profile.seasons = profile.seasons.map((season) => ({
          ...season,
          year_of_release: releaseYearValue(season.year_of_release)?.format("YYYY") ?? null,
          episodes: season.episodes.map((episode) => ({
            ...episode,
            release_date: episode.release_date
              ? moment(episode.release_date as string).format("YYYY-MM-DD")
              : null,
            duration: (() => {
              const parts = episodeDurationParts(episode.duration);
              if (!parts.mm && !parts.ss) return episode.duration;
              return episodeDurationValue(Number(parts.mm || 0), Number(parts.ss || 0));
            })(),
            crew: episode.crew.map((crew) => {
              const newCrew = { ...crew };
              delete newCrew.biography;
              delete (newCrew as any).filmography;
              return newCrew;
            }),
          })),
          awards: (season.awards ?? []).map((award) => ({
            ...award,
            award_year: award.award_year
              ? +(award.award_year as Dayjs).format("YYYY")
              : award.year
                ? +(award.year as Dayjs).format("YYYY")
                : +dayjs().format("YYYY"),
            country: award.country?.split("-")[1]?.toLowerCase(),
          })),
        }));

      const newProfileForm = new FormData();
      newProfileForm.append("data", JSON.stringify(profile));

      const appendPoster = (
        poster: Poster,
        type: "series" | "season" | "episode",
        kind: "vertical" | "horizontal",
        season?: number,
        episode?: number
      ) => {
        const rawFile = poster.file as File | Blob | null;
        const uploadFile =
          rawFile instanceof File
            ? rawFile
            : rawFile instanceof Blob
              ? new File([rawFile], "poster.jpg", {
                  type: rawFile.type || "image/jpeg",
                })
              : null;
        if (uploadFile) {
          const name = `${v4()}.${uploadFile.name.split(".").at(-1) || "jpg"}`;
          poster.file = new File([uploadFile], name, { type: uploadFile.type || "image/jpeg" });
          newProfileForm.append(
            `${type}_metadata`,
            JSON.stringify({
              media_type: "poster",
              aspect_ratio:
                kind === "vertical"
                  ? poster.type === 0
                    ? "3_4"
                    : poster.type === 1
                      ? "4_5"
                      : "1_1"
                  : poster.type === 0
                    ? "16_9"
                    : poster.type === 1
                      ? "4_3"
                      : "2_39_1",
              tags: [`${kind}-poster`],
              id: poster.id ?? name,
              name,
              season,
              episode,
            })
          );
          newProfileForm.append(`${type}_poster`, poster.file);
        } else if (poster.file == null && !!poster.url && poster.id != null) {
          newProfileForm.append(
            `${type}_metadata`,
            JSON.stringify({
              media_type: "poster",
              aspect_ratio:
                kind === "vertical"
                  ? poster.type === 0
                    ? "3_4"
                    : poster.type === 1
                      ? "4_5"
                      : "1_1"
                  : poster.type === 0
                    ? "16_9"
                    : poster.type === 1
                      ? "4_3"
                      : "2_39_1",
              tags: [`${kind}-poster`],
              id: poster.id,
              season,
              episode,
              image: poster.url,
            })
          );
          newProfileForm.append(`${type}_poster`, poster.url);
        }
      };

      for (const poster of verticalPoster) appendPoster(poster, "series", "vertical");
      for (const poster of horizontalPoster) appendPoster(poster, "series", "horizontal");

      let i = 0;
      for (const {
        vertical_poster: seasonVertical,
        horizontal_poster: seasonHorizontal,
        episodes,
      } of seasons) {
        for (const poster of seasonVertical) appendPoster(poster, "season", "vertical", i);
        for (const poster of seasonHorizontal) {
          appendPoster(poster, "season", "horizontal", i);
        }
        let j = 0;
        for (const {
          vertical_poster: episodeVertical,
          horizontal_poster: episodeHorizontal,
        } of episodes) {
          for (const poster of episodeVertical) appendPoster(poster, "episode", "vertical", i, j);
          for (const poster of episodeHorizontal) {
            appendPoster(poster, "episode", "horizontal", i, j);
          }
          j++;
        }
        i++;
      }

      try {
        const response = await (!!seriesId ? axios.put : axios.post)(
          "/client/series",
          newProfileForm,
          {
            headers: {
              ...((draft || editing) && seriesId ? { "X-Series-ID": seriesId } : {}),
            },
          }
        );

        handleResponse(response, async (res) => {
          const series = res.data.series;
          if (!series.seasons.length) {
            series.seasons = [
              {
                season_number: 1,
                id: v4(),
                modified: false,
                episodes: [{ id: v4(), episode_number: 1, modified: false }],
              },
            ] as any as Season[];
          }

          setHasUnsavedChanges(false);
          hasTriggeredAutosave.current = false;
          setSeriesObj(series);

          if (series.seasons?.length) {
            setSeasons((prev) => syncSeasonIdsFromApi(prev, series.seasons));
          }

          if (draft && editing) return;
          if (draft && !editing) {
            navigate(`/s/${res.data.series?.custom_name}/edit?draft=true`);
            return;
          }

          setShowSuccess(true);
          await wait(3000);

          if (!series.is_distributed) {
            modal.confirm({
              title: "Submit for distribution",
              content:
                "Do you want to submit this series for distribution? If you choose not to submit it now, you will sitll have the option of doing so later.",
              okText: "Submit for distribution",
              cancelText: "Later",
              onOk: async () => {
                navigate(`/s/${series.custom_name}/distribute`, {
                  replace: true,
                });
              },
              onCancel: () => {
                navigate(`/s/${series.custom_name}`, { replace: true });
              },
            });
          } else {
            navigate(`/s/${series.custom_name}`, { replace: true });
          }
        });

        return true;
      } catch (error) {
        handleResponse(error as any);
        return false;
      }
    },
    [
      customUrl,
      verticalPoster,
      horizontalPoster,
      form,
      seasons,
      synopsis,
      title,
      seriesId,
      modal,
      navigate,
    ]
  );

  useEffect(() => {
    const listener = professionalProfileSubject$
      .pipe(debounceTime(500))
      .subscribe((query) => {
        axios
          .get<
            {
              full_name: string;
              business_name?: string;
              custom_url: string;
              id: string;
              logo: string;
              profile_type?: "professional" | "business";
            }[]
          >(`/client/profile/search?q=${query}&type=${"all"}`)
          .then((response) => {
            setNameOptions(
              response.data.map((res) => ({
                value: res.full_name ?? res.business_name,
                label: (
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <img
                      src={res.logo ?? "/images/ui/avatar.png"}
                      alt="Profile Picture"
                      style={{
                        width: "2rem",
                        height: "2rem",
                        borderRadius: "50%",
                      }}
                    />
                    {res.full_name ?? res.business_name}
                    {/* <a href={res.custom_url} target="_blank">
                      See profile
                    </a> */}
                  </div>
                ) as any,
                ...res,
                full_name: res.full_name ?? res.business_name,
                type: res.profile_type,
              }))
            );
          });
      });

    return () => listener.unsubscribe();
  }, []);

  useEffect(() => {
    const listener = customUrlSubject$
      .pipe(debounceTime(500))
      .subscribe((value) => {
        setCustomUrl(value);
      });

    return () => listener.unsubscribe();
  }, [form]);

  // Track changes to mark form as having unsaved changes
  useEffect(() => {
    if (
      title.trim() ||
      synopsis.trim() ||
      customUrl.trim() ||
      verticalPoster.length ||
      horizontalPoster.length ||
      seasons.some((season) => season.modified)
    ) {
      setHasUnsavedChanges(true);
    }
  }, [title, synopsis, customUrl, seasons, verticalPoster, horizontalPoster]);

  // Autosave function for beforeunload
  const performAutosave = useCallback(async () => {
    if (isAutosaving || !title || !customUrl) return;

    setIsAutosaving(true);
    try {
      const saved = await onSubmit(!!seriesId, true);
      if (saved) {
        toast.success("Autosaved successfully!");
        setHasUnsavedChanges(false);
        hasTriggeredAutosave.current = true;
      } else {
        toast.error("Autosave failed. Please save manually.");
      }
    } catch (e) {
      console.error("Autosave failed:", e);
      toast.error("Autosave failed. Please save manually.");
    } finally {
      setIsAutosaving(false);
    }
  }, [isAutosaving, title, customUrl, seriesId, onSubmit]);

  // Beforeunload handler to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !hasTriggeredAutosave.current) {
        e.preventDefault();
        // Mark that we showed the beforeunload dialog
        beforeUnloadTriggered.current = true;
        // Modern browsers ignore custom messages, but this triggers the dialog
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    // When user clicks "Stay" in the dialog, the window regains focus
    const handleFocus = async () => {
      // Only autosave if beforeunload dialog was just shown and user stayed
      if (beforeUnloadTriggered.current && hasUnsavedChanges && !hasTriggeredAutosave.current) {
        beforeUnloadTriggered.current = false;
        // User chose to stay, perform autosave
        await performAutosave();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("focus", handleFocus);
    };
  }, [hasUnsavedChanges, performAutosave]);

  useEffect(() => {
    if (titleSpan.current) {
      const newWidth = titleSpan.current.offsetWidth + 1;
      setTitleWidth(
        newWidth <= window.innerWidth * 0.45
          ? newWidth < 275
            ? 275
            : newWidth
          : window.innerWidth * 0.45
      );
    }
  }, [title, titleSpan]);

  useEffect(() => {
    if (!customUrl) return;

    if (getCustomNameValidationError(customUrl)) {
      setUrlVerified(false);
      setShowVerification(true);
      return;
    }

    setShowVerification(false);

    axios
      .get(
        `/client/validate/custom-url/v2?q=${customUrl}&category=series` +
        (seriesObj?.id ? `&id=${seriesObj?.id}` : "")
      )
      .then(() => {
        setUrlVerified(true);
      })
      .catch(() => {
        setUrlVerified(false);
      })
      .finally(() => {
        setShowVerification(true);
      });
  }, [customUrl, seriesObj]);

  const isStepValid = useCallback(
    (step: number) => {
      if (step === 4) {
        return true;
      }

      const errors = form
        .getFieldsError()
        .filter((error) => error.errors.length > 0)
        .reduce(
          (map, error) => ({ ...map, [error.name.join("-")]: error }),
          {} as Record<string, any>
        );
      const currentSectionFields = Object.entries(sections)
        .filter(([, section]) => section === step)
        .map((el) => el[0]);
      let nFieldsTouched = 0;

      let isValid = true;

      for (const field of currentSectionFields) {
        nFieldsTouched +=
          form.isFieldTouched(field) || !!form.getFieldValue(field) ? 1 : 0;
        if (field in errors) {
          isValid = false;
          break;
        }
      }

      if (nFieldsTouched < currentSectionFields.length - 1) {
        isValid = false;
      }

      if (
        step === 1 &&
        (!hasPosterTypeZero(verticalPoster) ||
          !hasPosterTypeZero(horizontalPoster))
      ) {
        isValid = false;
      }

      if (
        step === 2 &&
        validateSeriesDraft({
          film: { ...form.getFieldsValue(), title },
          synopsis,
          verticalPoster,
          horizontalPoster,
          seasons,
        }).seasonErrors.length > 0
      ) {
        isValid = false;
      }

      if (
        step === 3 &&
        validateSeriesDraft({
          film: { ...form.getFieldsValue(), title },
          synopsis,
          verticalPoster,
          horizontalPoster,
          seasons,
        }).episodeErrors.length > 0
      ) {
        isValid = false;
      }

      return isValid;
    },
    [form, verticalPoster, horizontalPoster, title, seasons, synopsis]
  );

  const onStepChange = useCallback(
    (newStep: number, forceTabChange = false) => {
      if (currentStep === 2 && newStep > currentStep && stepFourTab === 0 && !forceTabChange) {
        setStepFourTab(1);
        return;
      }
      if (currentStep === 3 && newStep > currentStep && stepFiveTab < 2 && !forceTabChange) {
        setStepFiveTab((stepFiveTab + 1) as 0 | 1 | 2);
        return;
      }

      // Leaving the posters step: always allow navigation, but mark the sidebar
      // with a warning when required posters are still missing.
      if (currentStep === 1 && newStep !== 1) {
        const missingVertical = !hasPosterTypeZero(verticalPoster);
        const missingHorizontal = !hasPosterTypeZero(horizontalPoster);
        const newCompletedSteps = [...completedSteps];

        if (missingVertical || missingHorizontal) {
          setPosterErrors([missingVertical, missingHorizontal]);
          newCompletedSteps[1] = "warning";
        } else {
          setPosterErrors([false, false]);
          newCompletedSteps[1] = true;
        }

        setCompletedSteps(newCompletedSteps);
        setCurrentStep(newStep);
        return;
      }

      // Check for incomplete seasons when moving from seasons step (2) to episodes step (3)
      if (currentStep === 2 && newStep > currentStep) {
        const seasonErrors = validateSeriesDraft({
          film: { ...form.getFieldsValue(), title },
          synopsis,
          verticalPoster,
          horizontalPoster,
          seasons,
        }).seasonErrors;
        const incompleteSeasonIndex = seasonErrors
          .map((error) => error.seasonIndex)
          .find((index) => index > activeSeasonIndex);

        if (incompleteSeasonIndex !== undefined) {
          setActiveSeasonIndex(incompleteSeasonIndex);
          setStepFourTab(0);
          return;
        }
      }

      // Check for incomplete episodes when moving from episodes step (3) to review step (4)
      if (currentStep === 3 && newStep > currentStep) {
        const nextEpisodeError = validateSeriesDraft({
          film: { ...form.getFieldsValue(), title },
          synopsis,
          verticalPoster,
          horizontalPoster,
          seasons,
        }).episodeErrors.find(
          ({ seasonIndex, episodeIndex }) =>
            seasonIndex > activeSeasonIndex ||
            (seasonIndex === activeSeasonIndex && episodeIndex > activeEpisodeIndex)
        );

        if (nextEpisodeError) {
          setActiveSeasonIndex(nextEpisodeError.seasonIndex);
          setActiveEpisodeIndex(nextEpisodeError.episodeIndex);
          setStepFiveTab(tabForEpisodeErrors(nextEpisodeError.errors));
          return;
        }
      }

      const isValid = isStepValid(currentStep);
      const newCompletedSteps = [...completedSteps];
      newCompletedSteps[currentStep] = isValid;

      setCompletedSteps(newCompletedSteps);
      // setIsCurrentError(false);
      setCurrentStep(newStep);
    },
    [
      currentStep,
      form,
      completedSteps,
      stepFourTab,
      stepFiveTab,
      isStepValid,
      seasons,
      activeSeasonIndex,
      activeEpisodeIndex,
      synopsis,
      title,
      verticalPoster,
      horizontalPoster,
    ]
  );

  useEffect(() => {
    if (editing && !loaded) {
      axios
        .get<SeriesProfile>("/client/series", {
          headers: {
            "X-SERIES-ID": seriesId,
          },
        })
        .then((response) => {
          const film = { ...response.data };
          setSeriesObj(film);
          const newSeasons = film.seasons.map((season) => ({
            ...season,
            modified: true,
            awards: season.awards?.map((award) => ({
              ...award,
              award_year: award.award_year ? dayjs(award.award_year?.toString(), 'YYYY') : null,
            })),
            vertical_poster: season.season_image.filter((image) => image.tags?.includes("vertical-poster") || image.aspect_ratio === '9_16' || image.aspect_ratio === '1_1' || image.aspect_ratio === '3_4' || image.aspect_ratio === '4_5').map((poster) => ({
              ...poster,
              file: null,
              type: poster.aspect_ratio === '3_4' ? 0 : poster.aspect_ratio === '4_5' ? 1 : poster.aspect_ratio === '1_1' ? 2 : 3,
              url: poster.image
            })),
            horizontal_poster: season.season_image.filter((image) => image.tags?.includes("horizontal-poster") || image.aspect_ratio === '16_9' || image.aspect_ratio === '4_3' || image.aspect_ratio === '2_39_1').map((poster) => ({
              ...poster,
              file: null,
              type: poster.aspect_ratio === '16_9' ? 0 : poster.aspect_ratio === '4_3' ? 1 : 2,
              url: poster.image
            })),
            year_of_release: releaseYearValue(season.year_of_release),
            episodes: season.episodes.map((episode) => ({
              ...episode,
              modified: true,
              vertical_poster: episode.episode_image.filter((image) => image.tags?.includes("vertical-poster") || image.aspect_ratio === '9_16' || image.aspect_ratio === '1_1' || image.aspect_ratio === '3_4' || image.aspect_ratio === '4_5').map((poster) => ({
                ...poster,
                file: null,
                type: poster.aspect_ratio === '3_4' ? 0 : poster.aspect_ratio === '4_5' ? 1 : poster.aspect_ratio === '1_1' ? 2 : 3,
                url: poster.image
              })),
              horizontal_poster: episode.episode_image.filter((image) => image.tags?.includes("horizontal-poster") || image.aspect_ratio === '16_9' || image.aspect_ratio === '4_3' || image.aspect_ratio === '2_39_1').map((poster) => ({
                ...poster,
                file: null,
                type: poster.aspect_ratio === '16_9' ? 0 : poster.aspect_ratio === '4_3' ? 1 : 2,
                url: poster.image
              })),
              cast: (episode as any).episode_cast,
              crew: (episode as any).episode_crew,
              release_date: episode.release_date ? dayjs(episode.release_date?.toString(), 'DD/MM/YYYY') : null,
            })),
          } as any as Season));
          if (!newSeasons.length) {
            newSeasons.push(
              {
                season_number: 1,
                id: v4(),
                modified: false,
                season_image: [],
                episodes: [
                  {
                    id: v4(),
                    episode_number: 1,
                    modified: false,
                    cast: [],
                    crew: [],
                    episode_image: [],
                    vertical_poster: [],
                    horizontal_poster: [],
                    images: [],
                    ratings: {
                      censor: "",
                      adultContent: "",
                      language: "",
                      violence: "",
                      drugs: "",
                      harmful: "",
                      hateful: "",
                    }
                  }
                ],
                awards: [],
                vertical_poster: [],
                horizontal_poster: []
              } as any);
          }
          setSeasons(newSeasons as any as Season[]);
          for (const pendingChange of (film.pending_changes || [])) {
            film[pendingChange.field_name as any as keyof typeof film] =
              pendingChange.proposed_value as any as never;
          }

          form.setFieldsValue(film);
          if (film.custom_name) setCustomUrl(film.custom_name);
          setIsDraft(film.status === "draft");
          setIsDistributed(!!film.is_distributed);
          if (film.synopsis) setSynopsis(film.synopsis);

          for (const key in film) {
            if ((film as any)[key] === null) delete (film as any)[key];
          }
          if (film.title) {
            setTitle(film.title);
          }

          if (film.series_image) {
            const verticalPosters = film.series_image.filter(image => image.tags?.includes("vertical-poster") || image.aspect_ratio === '9_16' || image.aspect_ratio === '1_1' || image.aspect_ratio === '3_4' || image.aspect_ratio === '4_5').map(image => ({
              url: image.image,
              file: null,
              aspectRatio: image.aspect_ratio,
              type:
                image.aspect_ratio === "3_4"
                  ? 0
                  : image.aspect_ratio === "4_5"
                    ? 1
                    : 2,
              id: image.id.toString(),
            }));
            const horizontalPosters = film.series_image.filter(image => image.tags?.includes("horizontal-poster") || image.aspect_ratio === '16_9' || image.aspect_ratio === '4_3' || image.aspect_ratio === '2_39_1').map(image => ({
              url: image.image,
              file: null,
              aspectRatio: image.aspect_ratio,
              type: image.aspect_ratio === "16_9"
                ? 0
                : image.aspect_ratio === "4_3"
                  ? 1
                  : 2,
              id: image.id.toString(),
            }));

            setVerticalPoster(verticalPosters);
            setHorizontalPoster(horizontalPosters);
          }

          setLoaded(() => true);
        })
        .catch(handleResponse);
    }
  }, [form, editing, seriesId, isStepValid, loaded]);

  return (
    <Main>
      {modalConfig}
      <ProgressBar $progress={currentStep / 5} />
      <Splash
        show={showSuccess}
        title="Series created successfully!"
        icon={<MdCheckCircleOutline />}
      />
      <BackButton />
      <Form form={form}>
        {/* <Row
          style={{
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Column style={{ alignItems: "flex-start" }}>
            <span
              ref={titleSpan}
              style={{
                fontSize: "5.5rem",
                fontWeight: 700,
                wordSpacing: "1rem",
                position: "absolute",
                opacity: 0,
              }}
            >
              {title}
            </span>
            <UnderlinedInput
              placeholder="Enter title"
              $animate
              autoFocus={true}
              style={{ width: titleWidth }}
              value={title}
              onInput={(e) => {
                const target = e.target as HTMLInputElement;

                const newTitle = target.value;
                setTitle(newTitle);

                onSeriesTitleChange(newTitle);
                if (customUrlInput.current) {
                  autoResize(null, 30, 2, 10, customUrlInput.current);
                }
              }}
            />
            <Spacer direction="bottom" size={10} />
            <Form.Item
              label="English Title"
              initialValue=""
              name="english_title"
            >
              <UnderlinedInput
                name="english_title"
                placeholder="Enter English title"
                onInput={(e) => autoResize(autoCapitalize(e), 30, -3)}
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "400",
                  borderBottomWidth: "1px",
                  minWidth: "15ch",
                }}
              />
            </Form.Item>
          </Column>
          <Row style={{ alignItems: "center" }}>
            <URLVerified $verified={urlVerified} $show={showVerification}>
              {urlVerified === null ? (
                <MdLink color={colors.white.hex} size={20} />
              ) : urlVerified ? (
                <MdCheck color={colors.white.hex} size={20} />
              ) : (
                <MdRemove color={colors.white.hex} size={20} />
              )}
            </URLVerified>
            <Spacer direction="right" size={20} />
            <Row style={{ alignItems: "stretch", marginTop: 20 }}>
              <URLPrefix>https://pocketfilms.in/f/</URLPrefix>
              <Spacer direction="top" size={-3}>
                <Form.Item name="custom_name" initialValue="">
                  <UnderlinedInput
                    autoComplete="off"
                    name="custom_name"
                    ref={customUrlInput}
                    onInput={(e) =>
                      autoResize(
                        replaceChar(
                          replaceChar(
                            replaceChar(
                              setValue(e, customUrlSubject$),
                              " ",
                              ""
                            ),
                            "-",
                            ""
                          ),
                          ".",
                          ""
                        ),
                        30,
                        2
                      )
                    }
                    style={{
                      fontSize: "1.5rem",
                      borderBottomWidth: "1px",
                      borderBottomColor: colors.primary.hex,
                      borderBottomStyle: "dashed",
                      fontWeight: 400,
                    }}
                    onChange={(evt) => {
                      if (evt.target.value) {
                        form.setFields([{ name: "custom_name", errors: [] }]);
                      }
                    }}
                  />
                </Form.Item>
              </Spacer>
            </Row>
          </Row> */}
        <Spacer direction="bottom" size={20} />
        <FormRow>
          <Sidebar>
            <StepNav
              current={currentStep}
              completed={completedSteps}
              onChange={(index) => onStepChange(index, true)}
              items={[
                {
                  title: "Series Details",
                  description: "Basic info, ratings and synopsis.",
                },
                {
                  title: "Posters",
                  description: "Vertical and horizontal artwork.",
                },
                {
                  title: "Seasons",
                  description: "Season titles, posters and metadata.",
                },
                {
                  title: "Episodes",
                  description: "Episode details for each season.",
                },
                {
                  title: "Review",
                  description: "Confirm and submit your series.",
                },
              ]}
            />
            <NavCard>
              <NavRow>
                <Tooltip
                  title={currentStep > 0 ? "Ctrl + Shift + Enter" : ""}
                >
                  <NavButton
                    type="default"
                    icon={<MdArrowBack />}
                    disabled={currentStep === 0}
                    onClick={() => onStepChange(currentStep - 1)}
                  >
                    Previous
                  </NavButton>
                </Tooltip>
                {currentStep !== 4 ? (
                  <Tooltip title="Ctrl + Enter">
                    <NavButton
                      type="primary"
                      onClick={async () => {
                        onStepChange(currentStep + 1);

                        if (
                          (!seriesId || isDraft) &&
                          title &&
                          customUrl
                        ) {
                          await onSubmit(false, true);
                        }
                      }}
                    >
                      Next
                      <MdArrowForward />
                    </NavButton>
                  </Tooltip>
                ) : null}
              </NavRow>
            </NavCard>
          </Sidebar>
          <Column style={{ flex: 1, minWidth: 0, alignSelf: "stretch" }}>
            <Step
              {...{
                stepFourTab,
                setStepFourTab,
                stepFiveTab,
                setStepFiveTab,
                stepFourShowErrors,
                setStepFourShowErrors,
                posterErrors,
                activeEpisodeIndex,
                setActiveEpisodeIndex,
                setPosterErrors,
                seasonPosterErrors,
                setSeasonPosterErrors,
                episodePosterErrors,
                setEpisodePosterErrors,
                completedSteps,
                activeSeasonIndex,
                setCurrentStep,
                setActiveSeasonIndex,
                seasons,
                setSeasons,
                currentStep,
                verticalPoster,
                setVerticalPoster,
                horizontalPoster,
                setHorizontalPoster,
                synopsis,
                setSynopsis,
                nameOptions,
                professionalProfileSubject$,
                title,
                setTitle,
                form,
                customUrlSubject$,
                customUrlInputRef: customUrlInput,
                customUrl,
                urlVerified,
                showVerification,
                setCustomUrl,
                onSeriesTitleChange,
                titleFieldsAutoFocus: !seriesId,
              }}
            />
          </Column>
        </FormRow>
        <Spacer direction="bottom" size={30} />
      </Form>
      {currentStep === 4 ? (
        <SubmitButton
          label={!!seriesId ? "Save" : "Create"}
          onClick={() => {
            if (
              completedSteps
                .slice(0, -1)
                .filter((el) => el === false && el !== null).length
            ) {
              return toast.error(
                "Please review the errors in the form before proceeding!"
              );
            }
            onSubmit(!!seriesId, false);
          }}
        />
      ) : null}
    </Main>
  );
};

const Main = styled.div`
  display: flex;
  flex-direction: column;
  padding: clamp(1.6rem, 4vw, 4.5rem);
  padding-top: 8.5rem;
  padding-right: 8rem;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  background-color: ${colors.lightBackground.hex};

  .ant-form-item-has-error {
    margin-bottom: 9rem;
  }
`;

const FormItem = styled(Form.Item).attrs({ layout: "vertical" })`
  flex: 1 1 30rem;
  min-width: min(100%, 30rem);
  max-width: 100%;
  margin-bottom: 6rem;
`;

const FormCol = styled(Column)`
  width: 100%;
`;

const FormRow = styled(Row)`
  width: 100%;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: flex-start;
  flex-direction: row;

  margin-bottom: -1rem;

  @media only screen and (max-width: 48em) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  flex: 0 0 28rem;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2rem;
  margin: 0;
  padding: 0;

  @media only screen and (max-width: 48em) {
    flex: 1 1 100%;
    width: 100%;
  }
`;

const NavCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  box-shadow: ${card.shadow};
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
`;

const NavButton = styled(Button)`
  flex: 1 1 0;
  min-width: 0;
  height: 4rem;
  border-radius: 0.8rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &.ant-btn-primary {
    flex-grow: 1.4;
  }

  & svg {
    font-size: 1.6rem;
  }
`;

const URLVerified = styled.div<{
  $verified?: boolean | null;
  $show?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;

  opacity: ${({ $show }) => ($show ? 1 : 0)};

  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  background-color: ${({ $verified }) =>
    $verified === null
      ? colors.primary.hex
      : $verified
        ? colors.success.hex
        : colors.error.hex};

  transition: all 0.5s;
`;

const URLPrefix = styled.span`
  font-size: 1.5rem;
  font-weight: 500;
  text-decoration: underline;
  color: ${colors.primary.hex};
  cursor: pointer;
  text-underline-offset: 3.5px;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 0.5rem;
  background-color: ${colors.grey.hex};
  z-index: 3;

  &::after {
    content: "";
    text-align: center;
    vertical-align: center;
    color: ${colors.white.hex};
    font-size: 1.1rem;
    font-weight: 600;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ $progress }) => Math.round($progress * 100)}%;
    background-image: linear-gradient(-90deg, #438dc7 0%, #205496 100%);
    transition: all 0.5s;
  }
`;
